/**
 * V5 segment store — stores segment-based interpolation data per vehicle.
 *
 * Segment updates arrive via WebSocket alongside position updates.
 * The VehicleLayer uses segment data (when available) for route-following
 * interpolation instead of linear point-to-point animation.
 */

import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { interpolateFromTimeIntervals } from '../utils/segmentInterpolation';

// =============================================================================
// TYPES
// =============================================================================

export interface SegmentStop {
  name: string;
  gid: string;
}

/** A single time_intervals entry: [timestamp_ms, fraction, heading_rad | null] */
export type TimeInterval = [number, number, number | null];

export interface SegmentState {
  service_journey_id: string;
  journey_gid: string;
  state: 'BOARDING' | 'DRIVING';
  from_stop: SegmentStop;
  to_stop: SegmentStop;
  /** Segment polyline as [[lat, lon], ...] */
  segment_geometry: [number, number][];
  /** 4-entry geOps-style time_intervals */
  time_intervals: TimeInterval[];
  delay_seconds: number;
  has_realtime: boolean;
  data_source: string;
  line_name: string | null;
  transport_mode: string | null;
  direction: string | null;
  color_bg: string | null;
  /** When this segment was received (local timestamp) */
  receivedAt: number;
  /** Frozen visual position at transition start (for blend animation) */
  frozenBlendOrigin: [number, number] | null;
  /** performance.now() timestamp when segment transition began */
  transitionStartTime: number | null;
}

interface SegmentStoreState {
  /** Segments keyed by service_journey_id */
  segments: Map<string, SegmentState>;

  setSegment: (update: Omit<SegmentState, 'receivedAt' | 'frozenBlendOrigin' | 'transitionStartTime'>) => void;
  removeSegment: (id: string) => void;
  clearStaleSegments: (maxAgeMs?: number) => void;
}

// =============================================================================
// STORE
// =============================================================================

/** Max age for segment data before cleanup (2 minutes) */
const SEGMENT_MAX_AGE_MS = 120_000;

export const useSegmentStore = create<SegmentStoreState>()(
  immer((set) => ({
    segments: new Map(),

    setSegment: (update) => {
      set((state) => {
        const existing = state.segments.get(update.service_journey_id);

        let frozenBlendOrigin: [number, number] | null = null;
        let transitionStartTime: number | null = null;

        if (existing) {
          // Segment transition guard: skip blending if the road section changed
          const sameSection =
            existing.from_stop.gid === update.from_stop.gid &&
            existing.to_stop.gid === update.to_stop.gid;

          if (sameSection) {
            frozenBlendOrigin = interpolateFromTimeIntervals(
              existing.time_intervals,
              existing.segment_geometry,
              Date.now()
            );
          }

          // Only set transitionStartTime when frozenBlendOrigin is valid
          if (frozenBlendOrigin !== null) {
            transitionStartTime = performance.now();
          }
        }

        state.segments.set(update.service_journey_id, {
          ...update,
          receivedAt: Date.now(),
          frozenBlendOrigin,
          transitionStartTime,
        });
      });
    },

    removeSegment: (id) => {
      set((state) => {
        state.segments.delete(id);
      });
    },

    clearStaleSegments: (maxAgeMs = SEGMENT_MAX_AGE_MS) => {
      set((state) => {
        const now = Date.now();
        const toRemove: string[] = [];
        state.segments.forEach((seg, id) => {
          if (now - seg.receivedAt > maxAgeMs) {
            toRemove.push(id);
          }
        });
        toRemove.forEach((id) => state.segments.delete(id));
      });
    },
  }))
);

// =============================================================================
// SELECTORS
// =============================================================================

export const selectSegments = (state: SegmentStoreState) => state.segments;
